/* @jsxImportSource @emotion/react */

import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import type { ArticleAuthor, SanityTextContentProps } from '@slite/components'
import {
  ArticleCompanySummary,
  ArticleLayout,
  SanityTextRenderer,
} from '@slite/components'

import { NAVBAR_HEIGHT } from '../../../components/Navbar/NavbarSection'
import { Section } from '../../../components/Section'
import { SectionLastCTA } from '../../../components/SectionLastCTA'
import { SlitePage } from '../../../components/SlitePage'
import { useBanner } from '../../../components/useBanner'
import { WebsiteText } from '../../../components/WebsiteText'
import { useRoutes } from '../../../routes'
import { estimateReadingTimeMinutes } from '../../../utils/readingTime'
import { getSliteWebsiteConfig } from '../../data'
import { CustomerStoryGrid } from '../CustomerStoryGrid'
import { INDUSTRY_LABELS } from '../industries'
import type { Data, SliteCaseStudyData } from './+data'
import { CaseStudyImpacts } from './CaseStudyImpacts'

type Person = NonNullable<SliteCaseStudyData['author']>

function toArticleAuthor(person: Person): ArticleAuthor {
  return {
    name: person.name,
    imageUrl: person.picture?.url,
    bio: person.bio as SanityTextContentProps | null,
  }
}

// Editors type the bare domain, e.g. "plaid.com".
function toAbsoluteUrl(website: string) {
  return /^https?:\/\//.test(website) ? website : `https://${website}`
}

export default function CustomerStoryPage() {
  const intl = useIntl()
  const routes = useRoutes()
  const { navbarTopOffset } = useBanner()
  const { caseStudy, similarCaseStudies } = useData<Data>()

  const pageUrl = routes.customers.detail({ slug: caseStudy.slug.current })
  const content = (caseStudy.content ?? []) as SanityTextContentProps
  const readingTime = estimateReadingTimeMinutes(content)
  const publishedAt = caseStudy.date ?? caseStudy._updatedAt

  const headerPerson = caseStudy.contributor ?? caseStudy.author
  const bioPeople = [caseStudy.contributor, caseStudy.author].filter(
    (person): person is Person => person != null
  )
  const authorsLabel = caseStudy.contributor
    ? intl.formatMessage({
        id: 'website.customers.show.talkWith',
        defaultMessage: 'A talk with',
      })
    : intl.formatMessage({
        id: 'website.customers.show.writtenBy',
        defaultMessage: 'Written by',
      })

  const industryLabels = (caseStudy.industries ?? []).flatMap((industry) => {
    const label = INDUSTRY_LABELS[industry]
    return label ? [intl.formatMessage(label)] : []
  })

  const logo = caseStudy.companyLogo
  const heroImage = logo?.url
    ? {
        heroImageUrl: logo.url,
        heroImageAlt: logo.alt ?? '',
        heroImageFit: 'contain' as const,
        heroBackgroundColor: caseStudy.logoBackgroundColor ?? 'var(--shade-05)',
      }
    : {
        heroImageUrl: caseStudy.openGraphImage?.url ?? undefined,
        heroImageAlt: '',
      }

  return (
    <SlitePage
      background="moon-dust-lighter"
      footerBackground="moon-dust-lighter"
      canonicalUrl={pageUrl}
      title={caseStudy.title}
      description={
        caseStudy.metaDescription ??
        intl.formatMessage({
          id: 'website.customers.default.page.description',
          defaultMessage:
            'Hear from our customers about what makes Slite the better option for their team.',
        })
      }
      ogImage={
        caseStudy.openGraphImage?.url ??
        'https://cdn.sanity.io/images/dnsgqii5/production/14f802c185483f1cd39b27897cbd571ec3bb88a3-2400x1254.jpg'
      }
      additionalMetaTags={[{ name: 'og:type', content: 'article' }]}
    >
      <Section background="moon-dust-lighter" containerTopPadding={40}>
        <ArticleLayout
          title={caseStudy.title}
          date={intl.formatDate(publishedAt, { dateStyle: 'medium' })}
          dateTime={publishedAt}
          readingTime={intl.formatMessage(
            {
              id: 'website.customers.show.readingTime',
              defaultMessage: '{minutes} min read',
            },
            { minutes: readingTime }
          )}
          category={intl.formatMessage({
            id: 'website.customers.show.category',
            defaultMessage: 'Case study',
          })}
          authors={headerPerson ? [toArticleAuthor(headerPerson)] : []}
          bioAuthors={bioPeople.map(toArticleAuthor)}
          authorsLabel={authorsLabel}
          backLabel={intl.formatMessage({
            id: 'website.customers.show.allCustomers',
            defaultMessage: 'All customers',
          })}
          backHref={routes.customers.index()}
          ctaLabel={
            caseStudy.ctaLabel ??
            intl.formatMessage({
              id: 'website.customers.show.cta',
              defaultMessage: 'Talk to our team',
            })
          }
          ctaHref={caseStudy.ctaLink ?? routes.bookDemo()}
          showCtaInSidebar
          {...heroImage}
          belowHeader={
            caseStudy.impacts?.length ? (
              <CaseStudyImpacts impacts={caseStudy.impacts} />
            ) : undefined
          }
          sidebarHeader={
            <ArticleCompanySummary
              logoUrl={caseStudy.companyLogoOnWhite?.url ?? undefined}
              logoAlt={caseStudy.companyLogoOnWhite?.alt ?? undefined}
              websiteUrl={
                caseStudy.customerWebsite
                  ? toAbsoluteUrl(caseStudy.customerWebsite)
                  : undefined
              }
              websiteLabel={intl.formatMessage({
                id: 'website.customers.show.visitWebsite',
                defaultMessage: 'Visit website',
              })}
              description={
                caseStudy.companyDescription && (
                  <SanityTextRenderer
                    variant="website"
                    content={
                      caseStudy.companyDescription as SanityTextContentProps
                    }
                  />
                )
              }
              tags={industryLabels}
            />
          }
          shareUrl={`${getSliteWebsiteConfig(intl).domain}${pageUrl}`}
          tocLabel={intl.formatMessage({
            id: 'website.customers.show.toc',
            defaultMessage: 'Table of contents',
          })}
          shareLabel={intl.formatMessage({
            id: 'website.customers.show.share',
            defaultMessage: 'Share',
          })}
          shareOnXLabel={intl.formatMessage({
            id: 'website.customers.show.shareOnX',
            defaultMessage: 'Share on X',
          })}
          shareOnLinkedInLabel={intl.formatMessage({
            id: 'website.customers.show.shareOnLinkedIn',
            defaultMessage: 'Share on LinkedIn',
          })}
          stickyTopOffset={NAVBAR_HEIGHT + navbarTopOffset}
        >
          <SanityTextRenderer variant="website" content={content} />
        </ArticleLayout>
      </Section>

      {similarCaseStudies.length > 0 && (
        <Section background="moon-dust-lighter">
          <WebsiteText
            variant="title"
            className="text-align-center margin-bottom-40"
          >
            <FormattedMessage
              id="website.customers.similar.title"
              defaultMessage="Slite’s helped more {br} companies, read more:"
              values={{ br: <br /> }}
            />
          </WebsiteText>

          <CustomerStoryGrid
            caseStudies={similarCaseStudies}
            withWideCards={false}
            animateReveal={false}
          />
        </Section>
      )}
      <SectionLastCTA background="moon-dust-lighter" />
    </SlitePage>
  )
}
